import {
  all,
  call,
  getContext,
  put,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import {
  getProposalUsersSuccess,
  getProposalUsersFailure,
  getMoreProposalUsersSuccess,
  getMoreProposalUsersFailure,
  setProposalUsers,
  createProposalFailure,
  createProposalSuccess,
  editProposalSuccess,
  editProposalFailure,
  getProposalDetailsFailure,
  getProposalDetailsSuccess,
} from "./actions";
import { setAuthors } from "../authors/actions";
import {
  addConversationDetails,
  addConversation,
  addProposalMessage,
  getConversations,
} from "../conversations/actions";
import {
  createProposalActionTypes,
  getMoreProposalUsersActionTypes,
  getProposalUsersActionTypes,
  editProposalActionTypes,
  getProposalDetailsActionTypes,
} from "./actionTypes";
import { CONTEXT } from "@const/redux";
import { normalizeUserArray } from "@helpers/users";
import {
  normalizeMessage,
  normalizeConversationsArray,
} from "@helpers/conversations";

import { updatePost, setPosts } from "../posts/actions";
import { toCents } from "@helpers/price";
import { generateUniqueID } from "@helpers/uniqID";
import { limit } from "@const/chat";

const getProposalAsync = ({ ContentProposalService, params }) => {
  if (!ContentProposalService) {
    return;
  }

  return ContentProposalService.getRecipients(params);
};

function* getProposalUsers({ payload }) {
  try {
    const { ContentProposalService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getProposalAsync, {
      ContentProposalService,
      params: payload,
    });

    const { entities, result: allUsers } = normalizeUserArray(data);
    const { user } = entities;

    const { totalCount, nextOffset } = pagination;

    yield all([
      put(setProposalUsers(user)),
      put(getProposalUsersSuccess({ allUsers, totalCount, nextOffset })),
    ]);
  } catch ({ error }) {
    yield put(getProposalUsersFailure(error));
  }
}

function* getMoreProposalUsers({ payload }) {
  try {
    const { ContentProposalService } = yield getContext(CONTEXT.SERVICES);
    const { data, pagination } = yield call(getProposalAsync, {
      ContentProposalService,
      params: payload,
    });

    const { nextOffset, totalCount } = pagination;

    const { entities, result: allUsers } = normalizeUserArray(data);
    const { user } = entities;

    yield put(
      getMoreProposalUsersSuccess({ allUsers, user, nextOffset, totalCount }),
    );
  } catch ({ error }) {
    yield put(getMoreProposalUsersFailure(error));
  }
}

const createProposalAsync = ({ ContentProposalService, model }) =>
  ContentProposalService.createProposal(model);

const getConversationsDetailsAsync = ({ ConversationsService, params }) => {
  if (!ConversationsService) {
    console.log("Required service does not exist");
    return;
  }

  return ConversationsService.getConversationsByIds(params);
};

// todo: toast text, normalize and store data, redirect
function* createProposal({ payload }) {
  try {
    const { ContentProposalService, ConversationsService } = yield getContext(
      CONTEXT.SERVICES,
    );

    const { files, price, poster, ...values } = payload;

    const uuid = generateUniqueID();

    const model = {
      ...values,
      files,
      price: toCents(price),
      uuid,
    };

    const { data } = yield call(createProposalAsync, {
      ContentProposalService,
      model,
    });

    const {
      result: allMessages,
      entities: { messages: messagesById },
    } = normalizeMessage(data);

    const conversationsIds = data?.contentProposalConversationsIds;

    const { data: convDetails } = yield call(getConversationsDetailsAsync, {
      ConversationsService,
      params: { conversationsIds },
    });

    const {
      entities: { conversationOpponents, conversations: conversationsById },
    } = normalizeConversationsArray(convDetails);

    yield put(setAuthors(conversationOpponents));
    yield put(addConversationDetails(conversationsById));

    yield put(createProposalSuccess());
    yield put(getConversations({ limit }));

    for (let convId of conversationsIds) {
      yield put(addConversation({ conversationId: convId }));
    }

    if (!files?.length) {
      for (let convId of conversationsIds) {
        yield put(
          addProposalMessage({
            allMessages: [allMessages],
            messagesById,
            conversationId: convId,
          }),
        );
      }
    }
  } catch (error) {
    console.log(error);
    yield put(createProposalFailure());
  }
}

const editProposalAsync = ({ ContentProposalService, id, params }) => {
  if (!ContentProposalService) {
    return;
  }

  return ContentProposalService.editProposal(id, params);
};

function* editProposal({ payload }) {
  try {
    const { id, ...body } = payload;

    const { ContentProposalService } = yield getContext(CONTEXT.SERVICES);

    const { data } = yield call(editProposalAsync, {
      ContentProposalService,
      id,
      params: body,
    });

    const {
      entities: { post },
    } = normalizeMessage(data);

    yield put(
      updatePost({
        id: Object.values(post)[0].id,
        post: Object.values(post)[0],
      }),
    );

    yield put(editProposalSuccess());
  } catch (error) {
    yield put(editProposalFailure());
  }
}

const getProposalDetailsAsync = ({ ContentProposalService, id }) => {
  if (!ContentProposalService) {
    return;
  }

  return ContentProposalService.getProposalDetails(id);
};

function* getProposalDetails({ payload }) {
  try {
    const { id } = payload;

    const { ContentProposalService } = yield getContext(CONTEXT.SERVICES);

    const { data } = yield call(getProposalDetailsAsync, {
      ContentProposalService,
      id,
    });

    yield put(setPosts({ postById: { [data.id]: data } }));
    yield put(getProposalDetailsSuccess());
  } catch (error) {
    yield put(getProposalDetailsFailure());
  }
}

export function* watchGetProposalUsers() {
  yield takeLatest(getProposalUsersActionTypes.request, getProposalUsers);
}

export function* watchGetMoreProposalUsers() {
  yield takeLatest(
    getMoreProposalUsersActionTypes.request,
    getMoreProposalUsers,
  );
}

export function* watchCreateProposal() {
  yield takeEvery(createProposalActionTypes.request, createProposal);
}

export function* watchEditProposal() {
  yield takeEvery(editProposalActionTypes.request, editProposal);
}

export function* watchGetProposalDetails() {
  yield takeEvery(getProposalDetailsActionTypes.request, getProposalDetails);
}
